// The Teams screen's view model (TASK-578, SCR-573, API-008): what the table and dialogs show and
// decide, DOM-free. Rows and members are named for people — a person never reads a uuid here;
// the team id shown is the `externalId` the proxy sees in the `team_id` claim. Authorization
// stays the server's (the permission matrix); `canWriteTeams` only hides controls a principal
// could not use.
import { ApiError } from "../../api/errors";
import type { components } from "../../api/schema";
import { fieldErrorsFrom, lengthBetween, required, validate, type FieldErrors, type Rule } from "../../primitives/forms";
import type { ScopeMe } from "../../scope/scope";
import { personName } from "../../primitives/person-name";

export type Team = components["schemas"]["Team"];
export type User = components["schemas"]["User"];
export type CreateTeamRequest = components["schemas"]["CreateTeamRequest"];
export type PatchTeamRequest = components["schemas"]["PatchTeamRequest"];

export { personName, UNKNOWN_USER } from "../../primitives/person-name";

/** The one page size the screen reads with (API `limit` max 500). */
export const PAGE_LIMIT = 500;

// ---------------------------------------------------------------------------------------------
// Rows

export interface TeamRow {
  id: string;
  name: string;
  /** The id the proxy sees (`team_id` claim), never the uuid. */
  externalId: string;
  /** Members among the users loaded; null when the users could not be loaded. */
  members: number | null;
}

export function teamRows(teams: readonly Team[], users: readonly User[] | null): TeamRow[] {
  const counts = new Map<string, number>();
  for (const user of users ?? []) if (user.teamId) counts.set(user.teamId, (counts.get(user.teamId) ?? 0) + 1);
  return teams
    .map((team) => ({ id: team.id, name: team.name, externalId: team.externalId, members: users === null ? null : (counts.get(team.id) ?? 0) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export interface MemberRow {
  id: string;
  name: string;
  /** The email beside the name, when the name is not already the email. */
  email: string | null;
}

export function memberRows(users: readonly User[]): MemberRow[] {
  return users
    .map((user) => {
      const name = personName(user);
      return { id: user.id, name, email: user.email && user.email !== name ? user.email : null };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

// ---------------------------------------------------------------------------------------------
// Who may write (permission matrix: createTeam/patchTeam/deleteTeam allow org_admin, super_admin
// and an admin:write service principal; hidden from billing and installer principals).

export function canWriteTeams(me: ScopeMe): boolean {
  return me.role === "org-admin" || me.role === "super-admin" || me.permissions.includes("admin:write");
}

// ---------------------------------------------------------------------------------------------
// The create and rename forms

export type TeamFormMode = "create" | "rename";

export interface TeamFormValues {
  name: string;
  /** Only the create form has one; the API refuses changing it. */
  externalId: string;
}

const NAME_RULES: readonly Rule[] = [required, lengthBetween(1, 80)];
const EXTERNAL_ID_RULES: readonly Rule[] = [required, lengthBetween(1, 200)];

export function validateTeamForm(mode: TeamFormMode, values: TeamFormValues): FieldErrors {
  return validate(values, mode === "create" ? { name: NAME_RULES, externalId: EXTERNAL_ID_RULES } : { name: NAME_RULES });
}

export function toCreateRequest(values: TeamFormValues): CreateTeamRequest {
  return { externalId: values.externalId.trim(), name: values.name.trim() };
}

export function toRenameRequest(values: TeamFormValues): PatchTeamRequest {
  return { name: values.name.trim() };
}

// ---------------------------------------------------------------------------------------------
// Delete: only an empty team (the API answers 409 team_not_empty otherwise)

/** Why the team cannot be deleted right now, or null when it can. */
export function deleteBlockReason(members: number | null): string | null {
  if (members === null) return "The members could not be loaded, so the team cannot be deleted yet.";
  if (members === 0) return null;
  return `This team still has ${members} ${members === 1 ? "member" : "members"}. Move them to another team first.`;
}

// ---------------------------------------------------------------------------------------------
// Refused writes

export interface WriteFailure {
  /** The form field the refusal belongs to, or null for the form as a whole. */
  field: keyof TeamFormValues | null;
  message: string;
}

export function asApiError(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

/** A refused write in the screen's words: a duplicate id lands on its field, as does a field detail. */
export function writeFailure(error: ApiError): WriteFailure {
  switch (error.code) {
    case "team_exists":
      return { field: "externalId", message: "A team with this id already exists." };
    case "team_not_empty":
      return { field: null, message: "This team still has members. Move them to another team first." };
    case "forbidden":
      return { field: null, message: "Your role cannot make this change." };
  }
  const fields = fieldErrorsFrom(error);
  for (const field of ["name", "externalId"] as const) {
    const message = fields[field];
    if (message) return { field, message };
  }
  return { field: null, message: error.message };
}
